/**
 * @fileoverview Serialize a SignatureSpec into the `signatureBlock` HTML
 * element and parse it back, plus the shared static renderer used by the
 * NodeView and the PDF/Word exports, and the client-side resolver that fills
 * agent-authored placeholders with the current user's saved signature.
 *
 * The spec lives JSON-encoded in a single attribute so the canvas `html`
 * field round-trips unchanged (same scheme as `chartHtml.ts`). The signature
 * image is baked in as a data URI at resolve time — signed storage URLs
 * expire, and baking also freezes the signature as it was when the document
 * was signed.
 *
 * The agent can't see the user's signature, so it emits an *unresolved*
 * placeholder (`<div data-morgan-signature='{}'></div>`); `CanvasPanel`
 * resolves placeholders against the current user's saved signature
 * (`users.signatureImageId` / `signatureText`, from `getCurrentUser`).
 */

export interface SignatureSpec {
  /** Display name rendered under the signature. */
  name?: string;
  /** Signature image as a data URI, baked in at resolve time. */
  image?: string;
  /** Typed signature text fallback, rendered in cursive. */
  text?: string;
  /** Explicit pixel dimensions for the image (Word ignores max-* CSS). */
  widthPx?: number;
  heightPx?: number;
}

/** Base display box for a signature image at scale 1 (matches settings). */
export const SIG_MAX_HEIGHT_PX = 60;
export const SIG_MAX_WIDTH_PX = 240;
/** Capture width cap for the baked data URI (2x display for sharpness). */
const SIG_CAPTURE_MAX_WIDTH_PX = 720;
/** Give up on the image beyond this data-URI length; fall back to text. */
const SIG_MAX_DATA_URI_LENGTH = 600_000;

export function encodeSignatureAttr(spec: SignatureSpec): string {
  return JSON.stringify(spec).replace(/'/g, "&#39;").replace(/</g, "&lt;");
}

/**
 * Parse the attribute back to a spec. Unlike charts/spreadsheets, a
 * malformed or empty attribute parses to `{}` (an unresolved placeholder)
 * rather than `null`, so agent-authored placeholders survive the TipTap
 * round-trip instead of being stripped to a bare div.
 */
export function signatureSpecFromAttr(attr: string): SignatureSpec {
  try {
    const json = attr.replace(/&#39;/g, "'").replace(/&lt;/g, "<");
    const parsed: unknown = JSON.parse(json);
    if (typeof parsed !== "object" || parsed === null) return {};
    const source = parsed as Record<string, unknown>;
    const spec: SignatureSpec = {};
    if (typeof source.name === "string") spec.name = source.name;
    if (typeof source.image === "string") spec.image = source.image;
    if (typeof source.text === "string") spec.text = source.text;
    if (typeof source.widthPx === "number") spec.widthPx = source.widthPx;
    if (typeof source.heightPx === "number") spec.heightPx = source.heightPx;
    return spec;
  } catch {
    return {};
  }
}

/** Block element TipTap parses into a `signatureBlock` node. */
export function specToSignatureHtml(spec: SignatureSpec): string {
  return `<div data-morgan-signature='${encodeSignatureAttr(spec)}'></div>`;
}

/** A resolved spec has something to draw; an unresolved one is a placeholder. */
export function isResolvedSignatureSpec(spec: SignatureSpec): boolean {
  return Boolean(spec.image || spec.text);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Render a SignatureSpec to static markup for the print/Word exports.
 * Word's altChunk importer ignores `max-*` CSS, so the `word` target emits
 * the explicit pixel dimensions carried on the spec.
 */
export function signatureToHtml(
  spec: SignatureSpec,
  target: "print" | "word" = "print",
): string {
  const nameLine = spec.name
    ? `<p style="font-size:12px;margin:2px 0 10px;">${escapeHtml(spec.name)}</p>`
    : "";
  if (spec.image) {
    // Honour the spec's dimensions in both targets so a signature the user
    // resized in the canvas exports at the size they left it at.
    const sized =
      spec.widthPx && spec.heightPx
        ? target === "word"
          ? `width="${spec.widthPx}" height="${spec.heightPx}" style="width:${spec.widthPx}px;height:${spec.heightPx}px;"`
          : `style="width:${spec.widthPx}px;height:${spec.heightPx}px;object-fit:contain;"`
        : `style="max-height:${SIG_MAX_HEIGHT_PX}px;max-width:${SIG_MAX_WIDTH_PX}px;object-fit:contain;"`;
    return `<p style="margin:10px 0 0;"><img src="${spec.image}" alt="Signature" ${sized} /></p>${nameLine}`;
  }
  if (spec.text) {
    return `<p style="font-family:'Caveat',cursive;font-size:22px;line-height:1.4;margin:10px 0 0;">${escapeHtml(spec.text)}</p>${nameLine}`;
  }
  return `<p style="margin:10px 0 0;"><em>[Signature]</em></p>${nameLine}`;
}

/** The signature-bearing subset of `getCurrentUser`'s return value. */
export interface SignatureSource {
  firstName?: string;
  lastName?: string;
  email: string;
  signatureText?: string;
  signatureImageUrl?: string;
  signatureScale?: number;
}

/**
 * Build a resolved SignatureSpec from the current user's saved signature.
 * Returns null when the user has no signature configured (image or text).
 * Fetches and downscales the signature image into a data URI.
 */
export async function buildSignatureSpecForUser(
  user: SignatureSource,
): Promise<SignatureSpec | null> {
  const name =
    [user.firstName, user.lastName].filter(Boolean).join(" ") || user.email;
  const scale = user.signatureScale ?? 1;

  if (user.signatureImageUrl) {
    try {
      const image = await imageUrlToDataUri(user.signatureImageUrl);
      if (image && image.dataUri.length <= SIG_MAX_DATA_URI_LENGTH) {
        const maxW = SIG_MAX_WIDTH_PX * scale;
        const maxH = SIG_MAX_HEIGHT_PX * scale;
        const fit = Math.min(maxW / image.width, maxH / image.height, 1);
        return {
          name,
          image: image.dataUri,
          widthPx: Math.max(1, Math.round(image.width * fit)),
          heightPx: Math.max(1, Math.round(image.height * fit)),
        };
      }
    } catch {
      // fall through to the typed-signature fallback
    }
  }
  if (user.signatureText) {
    return { name, text: user.signatureText };
  }
  return null;
}

/**
 * Fetch an image and re-encode it as a PNG data URI, downscaled to a
 * reasonable capture width. Returns the *display* aspect via width/height
 * of the decoded source (CSS pixels are derived from these by the caller).
 */
async function imageUrlToDataUri(
  url: string,
): Promise<{ dataUri: string; width: number; height: number } | null> {
  const response = await fetch(url);
  if (!response.ok) return null;
  const blob = await response.blob();
  const bitmap = await createImageBitmap(blob);
  try {
    const ratio = Math.min(1, SIG_CAPTURE_MAX_WIDTH_PX / bitmap.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * ratio));
    canvas.height = Math.max(1, Math.round(bitmap.height * ratio));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return {
      dataUri: canvas.toDataURL("image/png"),
      width: bitmap.width,
      height: bitmap.height,
    };
  } finally {
    bitmap.close();
  }
}

const SIGNATURE_DIV_RE = /data-morgan-signature/;

/** Cheap check before running the async resolver. */
export function htmlHasSignaturePlaceholder(html: string): boolean {
  if (!SIGNATURE_DIV_RE.test(html)) return false;
  const root = document.createElement("div");
  root.innerHTML = html;
  return Array.from(root.querySelectorAll("div[data-morgan-signature]")).some(
    (el) =>
      !isResolvedSignatureSpec(
        signatureSpecFromAttr(el.getAttribute("data-morgan-signature") ?? ""),
      ),
  );
}

/**
 * Fill unresolved signature placeholders with the given resolved spec.
 * Leaves resolved blocks untouched. Returns the updated HTML and whether
 * anything changed.
 */
export function resolveSignaturePlaceholders(
  html: string,
  resolved: SignatureSpec,
): { html: string; changed: boolean } {
  if (!SIGNATURE_DIV_RE.test(html)) return { html, changed: false };
  const root = document.createElement("div");
  root.innerHTML = html;
  const changed = Array.from(
    root.querySelectorAll("div[data-morgan-signature]"),
  ).reduce((anyChanged, el) => {
    const spec = signatureSpecFromAttr(
      el.getAttribute("data-morgan-signature") ?? "",
    );
    if (isResolvedSignatureSpec(spec)) return anyChanged;
    // Raw JSON here — the DOM serializer escapes the attribute itself,
    // unlike `encodeSignatureAttr` which is for hand-built HTML strings.
    el.setAttribute("data-morgan-signature", JSON.stringify(resolved));
    return true;
  }, false);
  return { html: changed ? root.innerHTML : html, changed };
}
